import React, { useState, useContext } from 'react'
import styles from './styles.module.css'
import { cn } from '@s4l/utility-css'
import { FormContext } from '../context'
import type { Rule } from '../Validations'
import Typography from '../../Typography'
import useInputValue from '../hooks/useInputValue'

interface ICheckboxProps {
  name: string
  size?: 's' | 'l'
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  multiple?: boolean
  trueValue?: string | boolean | number
  falseValue?: string | boolean | number
  validations?: Array<ReturnType<Rule>>
}

const Checkbox = ({
  name,
  size = 's',
  defaultChecked,
  onChange,
  disabled,
  multiple,
  trueValue = true,
  falseValue = false,
  validations,
}: ICheckboxProps) => {
  const [checked, setChecked] = useState(defaultChecked)

  const { errors } = useContext(FormContext)

  const { onInputChange } = useInputValue({
    name,
    defaultValue: defaultChecked,
    multiple,
    trueValue,
    falseValue,
    validations,
    setInputValue: value => {
      if (typeof value === 'boolean') {
        setChecked(value)
      } else if (typeof value === 'string' || typeof value === 'number') {
        setChecked(value === trueValue)
      } else if (
        multiple &&
        Array.isArray(value) &&
        (typeof trueValue === 'string' || typeof trueValue === 'number')
      ) {
        setChecked(value.includes(trueValue))
      }
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(e.target.checked)
    setChecked(e.target.checked)
    onChange?.(e.target.checked)
  }

  const hasValidations = Array.isArray(validations) && validations.length > 0
  const hasError = !!errors[name]

  return (
    <>
      <input
        name={name}
        type="checkbox"
        className={cn(styles.checkbox, {
          [styles.big]: size === 'l',
          [styles.hasError]: hasError,
        })}
        checked={checked ?? false}
        data-state-validations={hasValidations}
        data-state-error={hasError}
        onChange={handleChange}
        disabled={disabled}
      />
      {hasError && (
        <Typography variant="caption" size="l" color="var(--color-error-500)" data-error-message>
          {errors[name]}
        </Typography>
      )}
    </>
  )
}

Checkbox.displayName = 'Form.Checkbox'
export default Checkbox
